/**
 * Hex lattice helpers (pointy-top axial, circumradius R).
 *   x = R·√3·(q + r/2) + ox,   y = 1.5·R·r + oy
 * `HexGeo` is derived once per WorldMap (cached) and maps lattice cells to land / water
 * indices so point → hex lookups, discs and rings are O(cells touched).
 */
import type { HexIdx, WaterIdx, WorldMap } from './world.js';

const SQRT3 = Math.sqrt(3);

/** axial neighbour directions; index matches the 6-slot neighbour arrays */
export const AXIAL_DIRS: readonly (readonly [number, number])[] = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

export interface HexGeo {
  readonly R: number;
  readonly ox: number;
  readonly oy: number;
  readonly qMin: number;
  readonly rMin: number;
  readonly qSpan: number;
  readonly rSpan: number;
  /** lattice cell → land index or -1 */
  readonly landAt: Int32Array;
  /** lattice cell → water index or -1 */
  readonly waterAt: Int32Array;
  /** water hex → up to 6 adjacent land hexes (M·6, -1 none) */
  readonly waterLand: Int32Array;
  /** connected component label per water hex (via wnbr, includes canals) */
  readonly waterComp: Int32Array;
}

const cache = new WeakMap<WorldMap, HexGeo>();

export function getGeo(world: WorldMap): HexGeo {
  let g = cache.get(world);
  if (!g) {
    g = buildGeo(world);
    cache.set(world, g);
  }
  return g;
}

function buildGeo(w: WorldMap): HexGeo {
  const R = w.hexR;
  const N = w.landCount;
  const M = w.waterCount;
  if (N === 0) throw new Error('WorldMap has no land hexes');
  const ox = w.cx[0] - R * SQRT3 * (w.q[0] + w.r[0] / 2);
  const oy = w.cy[0] - 1.5 * R * w.r[0];
  let qMin = Infinity;
  let qMax = -Infinity;
  let rMin = Infinity;
  let rMax = -Infinity;
  const tol = Math.max(1e-3, R * 0.02);
  const check = (q: number, r: number, x: number, y: number, what: string, i: number) => {
    const ex = R * SQRT3 * (q + r / 2) + ox;
    const ey = 1.5 * R * r + oy;
    if (Math.abs(ex - x) > tol || Math.abs(ey - y) > tol) {
      throw new Error(`WorldMap ${what} hex ${i} centre (${x},${y}) does not match pointy-top axial (${q},${r})`);
    }
    if (q < qMin) qMin = q;
    if (q > qMax) qMax = q;
    if (r < rMin) rMin = r;
    if (r > rMax) rMax = r;
  };
  for (let i = 0; i < N; i++) check(w.q[i], w.r[i], w.cx[i], w.cy[i], 'land', i);
  for (let i = 0; i < M; i++) check(w.wq[i], w.wr[i], w.wcx[i], w.wcy[i], 'water', i);
  // pad by one ring so neighbour lookups never fall off the table
  qMin -= 3;
  rMin -= 3;
  const qSpan = qMax - qMin + 4;
  const rSpan = rMax - rMin + 4;
  const landAt = new Int32Array(qSpan * rSpan).fill(-1);
  const waterAt = new Int32Array(qSpan * rSpan).fill(-1);
  for (let i = 0; i < N; i++) landAt[(w.r[i] - rMin) * qSpan + (w.q[i] - qMin)] = i;
  for (let i = 0; i < M; i++) waterAt[(w.wr[i] - rMin) * qSpan + (w.wq[i] - qMin)] = i;

  const waterLand = new Int32Array(M * 6).fill(-1);
  for (let i = 0; i < M; i++) {
    for (let d = 0; d < 6; d++) {
      const q = w.wq[i] + AXIAL_DIRS[d][0];
      const r = w.wr[i] + AXIAL_DIRS[d][1];
      const qi = q - qMin;
      const ri = r - rMin;
      if (qi < 0 || ri < 0 || qi >= qSpan || ri >= rSpan) continue;
      waterLand[i * 6 + d] = landAt[ri * qSpan + qi];
    }
  }

  const waterComp = new Int32Array(M).fill(-1);
  const stack: number[] = [];
  let comp = 0;
  for (let s = 0; s < M; s++) {
    if (waterComp[s] >= 0) continue;
    waterComp[s] = comp;
    stack.push(s);
    while (stack.length) {
      const i = stack.pop()!;
      for (let k = 0; k < 6; k++) {
        const j = w.wnbr[i * 6 + k];
        if (j >= 0 && waterComp[j] < 0) {
          waterComp[j] = comp;
          stack.push(j);
        }
      }
    }
    comp++;
  }
  return { R, ox, oy, qMin, rMin, qSpan, rSpan, landAt, waterAt, waterLand, waterComp };
}

/** Fractional point → rounded axial cell. */
export function axialOfPoint(g: HexGeo, x: number, y: number): [number, number] {
  const rf = (y - g.oy) / (1.5 * g.R);
  const qf = (x - g.ox) / (SQRT3 * g.R) - rf / 2;
  return cubeRound(qf, rf);
}

export function cubeRound(qf: number, rf: number): [number, number] {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return [q, r];
}

export function axialCenter(g: HexGeo, q: number, r: number): [number, number] {
  return [g.R * SQRT3 * (q + r / 2) + g.ox, 1.5 * g.R * r + g.oy];
}

/** lattice cell index or -1 if outside the table */
export function cellIndex(g: HexGeo, q: number, r: number): number {
  const qi = q - g.qMin;
  const ri = r - g.rMin;
  if (qi < 0 || ri < 0 || qi >= g.qSpan || ri >= g.rSpan) return -1;
  return ri * g.qSpan + qi;
}

export function landAtAxial(g: HexGeo, q: number, r: number): HexIdx {
  const c = cellIndex(g, q, r);
  return c < 0 ? -1 : g.landAt[c];
}

export function waterAtAxial(g: HexGeo, q: number, r: number): WaterIdx {
  const c = cellIndex(g, q, r);
  return c < 0 ? -1 : g.waterAt[c];
}

/** Land hex containing the point, or -1. */
export function landHexAt(g: HexGeo, x: number, y: number): HexIdx {
  const [q, r] = axialOfPoint(g, x, y);
  return landAtAxial(g, q, r);
}

/** Water hex containing the point, or -1. */
export function waterHexAt(g: HexGeo, x: number, y: number): WaterIdx {
  const [q, r] = axialOfPoint(g, x, y);
  return waterAtAxial(g, q, r);
}

export function axialDist(q1: number, r1: number, q2: number, r2: number): number {
  const dq = q1 - q2;
  const dr = r1 - r2;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

/** Axial distance between two land hexes. */
export function hexDist(w: WorldMap, a: HexIdx, b: HexIdx): number {
  return axialDist(w.q[a], w.r[a], w.q[b], w.r[b]);
}

/**
 * Land hexes whose centre lies within `radius` mu of (x, y). Appends to `out` and returns it.
 */
export function landInRadius(g: HexGeo, w: WorldMap, x: number, y: number, radius: number, out: HexIdx[] = []): HexIdx[] {
  const [q0, r0] = axialOfPoint(g, x, y);
  const k = Math.ceil(radius / (SQRT3 * g.R)) + 1;
  const r2 = radius * radius;
  for (let dr = -k; dr <= k; dr++) {
    const qLo = Math.max(-k, -dr - k);
    const qHi = Math.min(k, -dr + k);
    for (let dq = qLo; dq <= qHi; dq++) {
      const h = landAtAxial(g, q0 + dq, r0 + dr);
      if (h < 0) continue;
      const ddx = w.cx[h] - x;
      const ddy = w.cy[h] - y;
      if (ddx * ddx + ddy * ddy <= r2) out.push(h);
    }
  }
  return out;
}

/** Land hexes at exactly axial distance k from cell (q, r) (k = 0 → the cell itself). */
export function landRing(g: HexGeo, q: number, r: number, k: number, out: HexIdx[] = []): HexIdx[] {
  if (k === 0) {
    const h = landAtAxial(g, q, r);
    if (h >= 0) out.push(h);
    return out;
  }
  // start at direction 4 scaled by k, walk the 6 sides
  let cq = q + AXIAL_DIRS[4][0] * k;
  let cr = r + AXIAL_DIRS[4][1] * k;
  for (let side = 0; side < 6; side++) {
    for (let step = 0; step < k; step++) {
      const h = landAtAxial(g, cq, cr);
      if (h >= 0) out.push(h);
      cq += AXIAL_DIRS[side][0];
      cr += AXIAL_DIRS[side][1];
    }
  }
  return out;
}

/**
 * Nearest water hex to a point (searching up to `maxRings` lattice rings), or -1. With `comp`
 * ≥ 0 only hexes of that water component count (a ship's sea: a click near any of its coasts
 * finds it, never a landlocked pocket next to it).
 */
export function nearestWater(g: HexGeo, w: WorldMap, x: number, y: number, maxRings = 6, comp = -1): WaterIdx {
  const [q0, r0] = axialOfPoint(g, x, y);
  let best = -1;
  let bestD = Infinity;
  for (let k = 0; k <= maxRings; k++) {
    // scan ring k
    const cells: [number, number][] = [];
    if (k === 0) cells.push([q0, r0]);
    else {
      let cq = q0 + AXIAL_DIRS[4][0] * k;
      let cr = r0 + AXIAL_DIRS[4][1] * k;
      for (let side = 0; side < 6; side++) {
        for (let step = 0; step < k; step++) {
          cells.push([cq, cr]);
          cq += AXIAL_DIRS[side][0];
          cr += AXIAL_DIRS[side][1];
        }
      }
    }
    for (const [q, r] of cells) {
      const i = waterAtAxial(g, q, r);
      if (i < 0 || (comp >= 0 && g.waterComp[i] !== comp)) continue;
      const d = (w.wcx[i] - x) ** 2 + (w.wcy[i] - y) ** 2;
      if (d < bestD || (d === bestD && i < best)) {
        bestD = d;
        best = i;
      }
    }
    if (best >= 0) return best;
  }
  return -1;
}

export function dist(ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  return Math.sqrt(dx * dx + dy * dy);
}
